/**
 * Armadillo Security - Frontend Component Layer
 * Phoenix Architecture: Client-side security monitoring
 * 
 * Database → API Service (src/services/armadillo.ts) → This Component
 */

import { supabase } from '@/integrations/supabase/client';
import { armadilloAPI } from '@/services/armadillo';

export interface SecurityEvent {
  type: 'suspicious_activity' | 'rate_limit' | 'trust_change' | 'threat_detected';
  severity: 'low' | 'medium' | 'high' | 'critical';
  message: string;
  metadata?: Record<string, any>;
}

class ArmadilloSecurity {
  private static instance: ArmadilloSecurity;
  private requestTimestamps: number[] = [];
  private maxRequestsPerMinute = 60;
  private trustScore: number = 50;
  private isInitialized = false;

  private constructor() {}

  static getInstance(): ArmadilloSecurity {
    if (!ArmadilloSecurity.instance) {
      ArmadilloSecurity.instance = new ArmadilloSecurity();
    }
    return ArmadilloSecurity.instance;
  }

  /**
   * Initialize security monitoring
   */
  async initialize(userId?: string) {
    if (this.isInitialized) return;


    
    // Load trust score from localStorage
    const savedTrust = localStorage.getItem('armadillo_trust_score');
    if (savedTrust) {
      this.trustScore = parseInt(savedTrust);
    }

    // If user is logged in, load from database
    if (userId) {
      await this.loadTrustScore(userId);
    }

    this.isInitialized = true;

  }

  /**
   * Rate limiting check
   */
  checkRateLimit(): boolean {
    const now = Date.now();
    const oneMinuteAgo = now - 60000;

    // Remove old timestamps
    this.requestTimestamps = this.requestTimestamps.filter(t => t > oneMinuteAgo);

    // Check if limit exceeded
    if (this.requestTimestamps.length >= this.maxRequestsPerMinute) {
      this.logSecurityEvent({
        type: 'rate_limit',
        severity: 'medium',
        message: `Rate limit exceeded: ${this.requestTimestamps.length} requests/minute`
      });
      return false;
    }

    // Add current request
    this.requestTimestamps.push(now);
    return true;
  }

  /**
   * Track user activity and update trust score
   */
  trackActivity(userId: string, activity: string) {
    // Increase trust for normal activity
    this.trustScore = Math.min(100, this.trustScore + 0.1);
    this.saveTrustScore(userId);
  }

  /**
   * Decrease trust score for suspicious activity
   */
  decreaseTrust(userId: string, reason: string, amount: number = 5) {
    this.trustScore = Math.max(0, this.trustScore - amount);
    this.saveTrustScore(userId);

    this.logSecurityEvent({
      type: 'trust_change',
      severity: amount > 10 ? 'high' : 'medium',
      message: `Trust decreased by ${amount}: ${reason}`,
      metadata: { newScore: this.trustScore }
    });
  }

  /**
   * Log security event to Supabase
   */
  async logSecurityEvent(event: SecurityEvent) {


    try {
      const { data: { user } } = await supabase.auth.getUser();
      const userId = user?.id || null;
      
      // Log to Supabase database via API
      await armadilloAPI.logSecurityEvent(
        userId,
        event.type,
        event.severity,
        event.message,
        event.metadata
      );
      
      // Also store in localStorage for immediate debugging
      const logEntry = {
        timestamp: new Date().toISOString(),
        userId: userId || 'anonymous',
        ...event
      };

      const logs = JSON.parse(localStorage.getItem('armadillo_security_logs') || '[]');
      logs.push(logEntry);
      
      // Keep only last 100 logs
      if (logs.length > 100) {
        logs.splice(0, logs.length - 100);
      }
      
      localStorage.setItem('armadillo_security_logs', JSON.stringify(logs));
    } catch (error) {

    }
  }

  /**
   * Detect suspicious patterns
   */
  detectSuspiciousActivity(userId: string, pattern: string) {
    const suspiciousPatterns = [
      'rapid_clicks',
      'multiple_failed_logins',
      'unusual_navigation',
      'form_manipulation'
    ];

    if (suspiciousPatterns.includes(pattern)) {
      this.decreaseTrust(userId, `Suspicious pattern detected: ${pattern}`, 10);
      
      this.logSecurityEvent({
        type: 'suspicious_activity',
        severity: 'high',
        message: `Suspicious activity detected: ${pattern}`,
        metadata: { userId, pattern }
      });

      return true;
    }

    return false;
  }

  /**
   * Get current trust score
   */
  getTrustScore(): number {
    return this.trustScore;
  }

  /**
   * Get trust tier
   */
  getTrustTier(): { tier: number; name: string } {
    if (this.trustScore >= 86) return { tier: 5, name: 'Armadillo' };
    if (this.trustScore >= 71) return { tier: 4, name: 'Elevated' };
    if (this.trustScore >= 51) return { tier: 3, name: 'Trusted' };
    if (this.trustScore >= 31) return { tier: 2, name: 'Verified' };
    if (this.trustScore >= 11) return { tier: 1, name: 'Probationary' };
    return { tier: 0, name: 'Untrusted' };
  }

  /**
   * Save trust score to localStorage and database
   */
  private async saveTrustScore(userId: string) {
    localStorage.setItem('armadillo_trust_score', this.trustScore.toString());

    // Update in database if user is logged in
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ 
          updated_at: new Date().toISOString()
        })
        .eq('id', userId);

      if (error) {

      }
    } catch (error) {

    }
  }

  /**
   * Load trust score from database
   */
  private async loadTrustScore(userId: string) {
    try {
      // Try to get from trust_scores table first
      const trustData = await armadilloAPI.getTrustScore(userId);
      
      if (trustData) {
        this.trustScore = trustData.score;

      } else {
        // Initialize if doesn't exist
        const initialized = await armadilloAPI.initializeTrustScore(userId);
        if (initialized) {
          this.trustScore = initialized.score;

        }
      }
      
      this.saveTrustScore(userId);
    } catch (error) {

    }
  }

  /**
   * Get security statistics
   */
  getStatistics() {
    const logs = JSON.parse(localStorage.getItem('armadillo_security_logs') || '[]');
    
    return {
      trustScore: this.trustScore,
      trustTier: this.getTrustTier(),
      totalEvents: logs.length,
      recentEvents: logs.slice(-10),
      rateLimitStatus: {
        currentRequests: this.requestTimestamps.length,
        maxRequests: this.maxRequestsPerMinute,
        window: '1 minute'
      }
    };
  }
}

// Export singleton instance
export const armadilloSecurity = ArmadilloSecurity.getInstance();
